// frontend-react/src/components/sales/CartRow.jsx
import React, { memo } from 'react';
import {
  variantTypeLabel,
  buildVariantsFromComposition,
} from './variants.js';

function fmtXof(n) {
  return Number(n || 0).toLocaleString('fr-FR');
}

function CartRow({
  line,
  product,
  variantsCache,
  onRemove,
  onUpdateQty,
  onUpdateVariant,
  onUpdateNotes,
  renderVariantOptionLabel,
}) {
  const cached = variantsCache[line.productId] || { byType: {} };
  const byType = cached.byType || {};
  const rulesByType = cached.rulesByType || {};
  const types = Object.keys(byType);

  const basePU = Number(product?.unit_price ?? 0);
  const deltaUnit = Number(line.unitPrice || 0) - (Number.isFinite(basePU) ? basePU : 0);

  return (
    <li className="py-4">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <div>
          <div className="font-semibold">{line.name}</div>
          <div className="text-xs text-content-secondary">
            Qté {line.qty} - PU {fmtXof(line.unitPrice || 0)} XOF
            {Number.isFinite(basePU) && basePU > 0 && (
              <>
                {' '}- Base {fmtXof(basePU)} {deltaUnit > 0 ? `+ Options ${fmtXof(deltaUnit)}` : ''}
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-content-secondary">Qté</span>
            <input
              type="number"
              min={1}
              value={line.qty}
              onChange={e => onUpdateQty(line.id, e.target.value)}
              className="border rounded-lg px-2 py-1 w-24"
            />
          </div>

          <div className="font-semibold">
            {fmtXof(Number(line.qty || 0) * Number(line.unitPrice || 0))} XOF
          </div>

          <button onClick={() => onRemove(line.id)} className="text-red-600 text-sm">
            Supprimer
          </button>
        </div>
      </div>

      {/* Variants UI */}
      <div className="mt-3 p-3 border rounded-xl bg-surface-primary">
        <div className="font-semibold mb-2">Options (variants)</div>

        {types.length === 0 ? (
          <div className="text-sm text-content-secondary">
            Aucun variant pour ce produit (ou endpoint variants non trouvé).
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {types.map(t => {
              const listRaw = byType[t] || [];
              const list =
                t === 'nb_bougies'
                  ? listRaw
                      .slice()
                      .sort((a, b) => Number(a?.code ?? 0) - Number(b?.code ?? 0))
                  : listRaw;
              const current = line.selectedVariants?.[t] || '';
              const isSansBougies = String(line.selectedVariants?.type_bougie || '') === 'SANS';
              const isNbBougies = t === 'nb_bougies';
              return (
                <div key={t}>
                  <label className="text-xs text-content-secondary">{variantTypeLabel(t, rulesByType)}</label>
                  <select
                    value={current}
                    onChange={e => onUpdateVariant(line.id, t, e.target.value)}
                    disabled={isNbBougies && isSansBougies}
                    className="border px-2 py-2 rounded-lg w-full bg-surface-card"
                  >
                    {list.map(v => (
                      <option key={`${v.variant_type}-${v.code}`} value={v.code}>
                        {renderVariantOptionLabel(v, t, line.selectedVariants)}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        )}

        {/* Notes */}
        <div className="mt-3">
          <label className="text-xs text-content-secondary">Notes (optionnel)</label>
          <textarea
            className="border px-2 py-2 rounded-lg w-full bg-surface-card pilot-input"
            placeholder="Ex: livraison, remarque, etc."
            value={line.composition?.notes || ''}
            onChange={e => onUpdateNotes(line.id, e.target.value)}
          />
        </div>

        {/* Debug */}
        <details className="mt-3">
          <summary className="text-xs text-content-secondary cursor-pointer">Voir payload variants (debug)</summary>
          <pre className="text-xs bg-surface-card border rounded-lg p-2 mt-2 overflow-auto">
            {JSON.stringify(
              {
                product_id: line.productId,
                qty: line.qty,
                variants: buildVariantsFromComposition(line.composition, byType),
                composition: line.composition,
                computed_unit_price: line.unitPrice,
              },
              null,
              2
            )}
          </pre>
        </details>
      </div>

      {product && (
        <div className="mt-2 text-xs text-content-secondary">
          Stock actuel (front): {Number(product?.stock_quantity ?? product?.stock ?? 0)}
        </div>
      )}
    </li>
  );
}

export default memo(CartRow, (prevProps, nextProps) => {
  return (
    prevProps.line.id === nextProps.line.id &&
    prevProps.line.qty === nextProps.line.qty &&
    prevProps.line.unitPrice === nextProps.line.unitPrice &&
    JSON.stringify(prevProps.line.selectedVariants) === JSON.stringify(nextProps.line.selectedVariants) &&
    prevProps.line.composition?.notes === nextProps.line.composition?.notes
  );
});
